/**
 * Region Pack for Australia (AU)
 * 
 * This module implements Australian-specific financial rules, formatting, and conventions
 * as required by the MoneyMojo Constitution (Section VI - Regional Modularity).
 * 
 * @see .specify/memory/constitution.md
 * @see /Reference/Reference design-review/design-principles.md
 */

export interface RegionPack {
  /** ISO country code */
  countryCode: string;
  /** Country name */
  countryName: string;
  /** Default currency */
  currency: {
    code: string;
    symbol: string;
    decimalPlaces: number;
  };
  /** Date formatting */
  dateFormat: {
    display: string;
    input: string;
    parse: string;
  };
  /** Financial year settings */
  financialYear: {
    startMonth: number; // 1-based month (1 = January)
    startDay: number;   // 1-based day
    endMonth: number;   // 1-based month
    endDay: number;     // 1-based day
  };
  /** Tax settings */
  tax: {
    gstRate: number; // GST rate as decimal (0.10 = 10%)
    gstInclusive: boolean; // Whether prices are typically GST inclusive
  };
  /** Banking formats */
  banking: {
    supportedFormats: string[];
    defaultDateFormat: string;
    currencyFormat: string;
  };
  /** Localization */
  i18n: {
    language: string;
    locale: string;
    timezone: string;
  };
  /** Utility functions */
  formatCurrency: (amount: number, options?: {
    showSymbol?: boolean;
    showDecimals?: boolean;
    absolute?: boolean;
  }) => string;
  formatDate: (date: Date | string, format?: 'display' | 'input' | 'parse') => string;
  parseDate: (dateString: string) => Date | null;
  isInFinancialYear: (date: Date | string, fyStartYear: number) => boolean;
  getFinancialYear: (date: Date | string) => number;
  calculateGST: (amount: number, isGSTInclusive?: boolean) => number;
  formatGST: (amount: number, isGSTInclusive?: boolean) => string;
  formatTime: (date: Date | string, options?: {
    includeSeconds?: boolean;
    use24Hour?: boolean;
  }) => string;
  validateBankAccount: (accountNumber: string) => boolean;
  formatBankAccount: (accountNumber: string) => string;
}

/**
 * Australian Region Pack (data only - use createAURegionPack() for complete pack)
 * Implements Australian financial conventions as per Constitution requirements
 */
const AU_REGION_PACK_DATA = {
  countryCode: 'AU',
  countryName: 'Australia',
  
  currency: {
    code: 'AUD',
    symbol: '$',
    decimalPlaces: 2,
  },
  
  dateFormat: {
    display: 'DD/MM/YYYY', // Australian standard
    input: 'DD/MM/YYYY',
    parse: 'dd/MM/yyyy',
  },
  
  financialYear: {
    startMonth: 7,  // July
    startDay: 1,   // 1st
    endMonth: 6,    // June
    endDay: 30,     // 30th
  },
  
  tax: {
    gstRate: 0.10, // 10% GST
    gstInclusive: true, // Australian prices typically include GST
  },
  
  banking: {
    supportedFormats: ['CSV', 'OFX', 'QIF'],
    defaultDateFormat: 'DD/MM/YYYY',
    currencyFormat: '$#,##0.00',
  },
  
  i18n: {
    language: 'en',
    locale: 'en-AU',
    timezone: 'Australia/Sydney',
  },
};

/**
 * Currency formatting utilities for Australian context
 */
export const formatCurrency = (amount: number, options?: {
  showSymbol?: boolean;
  showDecimals?: boolean;
  absolute?: boolean;
}): string => {
  const { showSymbol = true, showDecimals = true, absolute = false } = options || {};
  
  const value = absolute ? Math.abs(amount) : amount;
  const formatted = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: showDecimals ? 2 : 0,
    maximumFractionDigits: showDecimals ? 2 : 0,
  }).format(value);
  
  return showSymbol ? formatted : formatted.replace('$', '').trim();
};

/**
 * Date formatting utilities for Australian context
 */
export const formatDate = (date: Date | string, format: 'display' | 'input' | 'parse' = 'display'): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) {
    return '';
  }
  
  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear();
  
  switch (format) {
    case 'display':
    case 'input':
      return `${day}/${month}/${year}`;
    case 'parse':
      return `${day}/${month}/${year}`;
    default:
      return `${day}/${month}/${year}`;
  }
};

/**
 * Parse Australian date format (DD/MM/YYYY)
 */
export const parseDate = (dateString: string): Date | null => {
  if (!dateString) return null;
  
  // Handle DD/MM/YYYY format
  const parts = dateString.split('/');
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // JavaScript months are 0-based
    const year = parseInt(parts[2], 10);
    
    const date = new Date(year, month, day);
    
    // Validate the date
    if (
      date.getDate() === day &&
      date.getMonth() === month &&
      date.getFullYear() === year
    ) {
      return date;
    }
  }
  
  return null;
};

/**
 * Check if a date falls within the Australian financial year
 */
export const isInFinancialYear = (date: Date | string, fyStartYear: number): boolean => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const fyStart = new Date(fyStartYear, AU_REGION_PACK.financialYear.startMonth - 1, AU_REGION_PACK.financialYear.startDay);
  const fyEnd = new Date(fyStartYear + 1, AU_REGION_PACK.financialYear.endMonth - 1, AU_REGION_PACK.financialYear.endDay);
  
  return dateObj >= fyStart && dateObj <= fyEnd;
};

/**
 * Get the financial year for a given date
 */
export const getFinancialYear = (date: Date | string): number => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const year = dateObj.getFullYear();
  const month = dateObj.getMonth() + 1; // Convert to 1-based month
  
  // If the date is before July, it belongs to the previous financial year
  if (month < AU_REGION_PACK.financialYear.startMonth) {
    return year - 1;
  }
  
  return year;
};

/**
 * Calculate GST amount
 */
export const calculateGST = (amount: number, isGSTInclusive: boolean = true): number => {
  if (isGSTInclusive) {
    // GST is included in the amount, so calculate the GST component
    return amount - (amount / (1 + AU_REGION_PACK.tax.gstRate));
  } else {
    // GST is not included, so add it
    return amount * AU_REGION_PACK.tax.gstRate;
  }
};

/**
 * Format GST amount for display
 */
export const formatGST = (amount: number, isGSTInclusive: boolean = true): string => {
  const gstAmount = calculateGST(amount, isGSTInclusive);
  return formatCurrency(gstAmount, { showSymbol: true, showDecimals: true });
};

/**
 * Get current Australian timezone
 */
export const getCurrentTimezone = (): string => {
  return AU_REGION_PACK.i18n.timezone;
};

/**
 * Format time for Australian context
 */
export const formatTime = (date: Date | string, options?: {
  includeSeconds?: boolean;
  use24Hour?: boolean;
}): string => {
  const { includeSeconds = false, use24Hour = true } = options || {};
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  return new Intl.DateTimeFormat('en-AU', {
    timeZone: AU_REGION_PACK.i18n.timezone,
    hour: '2-digit',
    minute: '2-digit',
    second: includeSeconds ? '2-digit' : undefined,
    hour12: !use24Hour,
  }).format(dateObj);
};

/**
 * Validate Australian bank account number format
 * Note: This is a basic validation - real validation would require bank-specific rules
 */
export const validateBankAccount = (accountNumber: string): boolean => {
  // Basic validation: 6-10 digits
  const cleaned = accountNumber.replace(/\s/g, '');
  return /^\d{6,10}$/.test(cleaned);
};

/**
 * Format Australian bank account number for display
 */
export const formatBankAccount = (accountNumber: string): string => {
  const cleaned = accountNumber.replace(/\s/g, '');
  
  // Format as XXX-XXX-XXX for 9 digits, or XXX-XXXX for 8 digits, etc.
  if (cleaned.length === 9) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned.length === 8) {
    return `${cleaned.slice(0, 4)}-${cleaned.slice(4)}`;
  } else if (cleaned.length === 7) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
  } else if (cleaned.length === 6) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
  }
  
  return cleaned;
};

/**
 * Create a complete region pack with all functions
 * This is what RegionProvider expects
 */
export const createAURegionPack = (): RegionPack => ({
  ...AU_REGION_PACK_DATA,
  formatCurrency,
  formatDate,
  parseDate,
  isInFinancialYear,
  getFinancialYear,
  calculateGST,
  formatGST,
  formatTime,
  validateBankAccount,
  formatBankAccount,
});

// Export the region pack as default (for backward compatibility)
export const AU_REGION_PACK = createAURegionPack();
export default AU_REGION_PACK;

// Reporting (Region Pack extension — 013-reporting-as-i)
export {
  resolveReportDateRange,
  groupByDeductionTheme,
  buildGstSummary,
  getAccountTypeLabel,
} from './au/reporting';
export type { CategoryLike, TransactionLike } from './au/reporting';
